import { useMemo } from 'react';
import {
  hasAcademyManagementRole,
  hasClubManagementRole,
  hasRole,
} from '@court-sync/shared';
import {
  getNavItemsByRole,
  getUnifiedManagementNavItems,
  getUnifiedManagementNavStructure,
  isOrganizationManagementRole,
  type SidebarNavItem,
  type UnifiedManagementNavStructure,
} from '@/components/layout/sidebar-nav-config';
import {
  getUnifiedManagementMobileTabItems,
  type MobileTabItem,
} from '@/components/layout/mobile-tab-nav-config';
import { useAuthStore } from '@/store/auth';
import { useClubModuleAccess } from '@/hooks/useClubModuleAccess';
import { useMatchPlayAccess } from '@/hooks/useMatchPlayAccess';
import type { DashboardRole } from '@/lib/dashboardBreadcrumbs';

export function useUnifiedManagementNav(dashboardRole: DashboardRole): {
  navItems: SidebarNavItem[];
  navStructure: UnifiedManagementNavStructure | null;
  mobileTabItems: MobileTabItem[];
  showAcademySelector: boolean;
  showClubSelector: boolean;
} {
  const { user } = useAuthStore();
  const { hasClubManagement, isLoading: clubEntitlementsLoading } = useClubModuleAccess();
  const { hasAccess: hasMatchPlay, isLoading: matchPlayLoading } = useMatchPlayAccess();

  return useMemo(() => {
    if (!user || !isOrganizationManagementRole(dashboardRole)) {
      return {
        navItems: getNavItemsByRole(dashboardRole as Parameters<typeof getNavItemsByRole>[0]),
        navStructure: null,
        mobileTabItems: [],
        showAcademySelector: false,
        showClubSelector: false,
      };
    }

    const hasAcademyManagement = hasAcademyManagementRole(user);
    const hasClubRole = hasClubManagementRole(user);
    const isAcademyOwner = hasRole(user, 'academy_owner');
    const isClubOwner = hasRole(user, 'club_owner');
    // Academy owners are org owners for the internal club shell; club nav does not require club_owner role.
    const canOperateClub = hasClubRole || isAcademyOwner;
    const hasClubModule =
      canOperateClub && hasClubManagement && !clubEntitlementsLoading;

    const navOptions = {
      hasAcademyManagement,
      hasClubModule,
      hasClubRole,
      canOperateClub,
      isAcademyOwner,
      isClubOwner,
      hasMatchPlay: hasMatchPlay && !matchPlayLoading,
    };

    return {
      navItems: getUnifiedManagementNavItems(navOptions),
      navStructure: getUnifiedManagementNavStructure(navOptions),
      mobileTabItems: getUnifiedManagementMobileTabItems(navOptions),
      showAcademySelector: hasAcademyManagement && hasRole(user, 'academy_owner'),
      showClubSelector: canOperateClub && hasClubModule,
    };
  }, [user, dashboardRole, hasClubManagement, clubEntitlementsLoading, hasMatchPlay, matchPlayLoading]);
}
